import { Command, CommandItem, CommandGroup } from '@src/design-system/command';
import { useCallback, useEffect, useState } from 'react';

import type { TMemberMini } from '@shared/types/member';
import { cn } from '@src/design-system/helpers';

export const MentionList = (props: {
  items: TMemberMini[];
  command: ({ id, label }: { id: number; label: string }) => void;
}) => {
  const selectItem = (index: number) => {
    const item = props.items[index];
    if (!item) return;
    props.command({
      id: item.id,
      label: item.first_name,
    });
  };
  const { selectedIndex } = useKeyboardNavigation(
    props.items.length,
    selectItem
  );

  return (
    <Command className='w-48 rounded-lg border shadow-md bg-white border-pale-border z-[99999]'>
      <CommandGroup>
        {props.items.length ? (
          props.items.map((item, index: number) => (
            <CommandItem
              className={cn(
                index === selectedIndex && 'bg-primary-50 text-primary-1000'
              )}
              key={index}
              onSelect={() => selectItem(index)}
            >
              {item.first_name}
            </CommandItem>
          ))
        ) : (
          <CommandItem disabled>No results</CommandItem>
        )}
      </CommandGroup>
    </Command>
  );
};

function useKeyboardNavigation(
  itemsLength: number,
  onSelect: (index: number) => void
) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const upHandler = useCallback(
    () => setSelectedIndex((prev) => (prev + itemsLength - 1) % itemsLength),
    [itemsLength]
  );
  const downHandler = useCallback(
    () => setSelectedIndex((prev) => (prev + 1) % itemsLength),
    [itemsLength]
  );
  const enterHandler = useCallback(
    () => onSelect(selectedIndex),
    [onSelect, selectedIndex]
  );

  useEffect(() => setSelectedIndex(0), [itemsLength, setSelectedIndex]);

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp') {
        upHandler();
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        downHandler();
        e.preventDefault();
      } else if (e.key === 'Enter') {
        enterHandler();
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', listener, { capture: true });
    return () =>
      document.removeEventListener('keydown', listener, { capture: true });
  }, [selectedIndex, itemsLength, upHandler, downHandler, enterHandler]);

  return {
    selectedIndex,
    setSelectedIndex,
    upHandler,
    downHandler,
    enterHandler,
  };
}
